import { z } from 'zod';
import type { UserRole } from './enums.js';

/**
 * Permissions granulaires du back-office BCL.
 *
 * Elles s'appliquent UNIQUEMENT aux comptes staff de rôle `admin` : le
 * super_admin attribue (coche) à chaque admin ce qu'il peut consulter / gérer.
 * Le `super_admin` dispose implicitement de TOUTES les permissions ; les rôles
 * `client`, `technicien` et `partenaire` ont un périmètre fixe géré par leur
 * rôle et n'utilisent pas ce système.
 */
export const permissionSchema = z.enum([
  'clients.view',
  'clients.manage',
  'projects.view',
  'projects.manage',
  'documents.review',
  'payments.view',
  'payments.validate',
  'invoices.view',
  'planning.manage',
  'reports.manage',
  'packs.manage',
  'exports.view',
  'partners.manage',
]);
export type Permission = z.infer<typeof permissionSchema>;

/** Liste exhaustive des permissions (ordre stable pour l'UI). */
export const ALL_PERMISSIONS: readonly Permission[] = permissionSchema.options;

export type PermissionCatalogGroup = {
  label: string;
  permissions: { key: Permission; label: string }[];
};

/** Catalogue groupé, source de vérité pour les cases à cocher du back-office. */
export const PERMISSION_CATALOG: readonly PermissionCatalogGroup[] = [
  {
    label: 'Clients',
    permissions: [
      { key: 'clients.view', label: 'Consulter les clients' },
      { key: 'clients.manage', label: 'Modifier les clients' },
    ],
  },
  {
    label: 'Dossiers',
    permissions: [
      { key: 'projects.view', label: 'Consulter les dossiers' },
      { key: 'projects.manage', label: 'Gérer les dossiers (statuts, affectation)' },
    ],
  },
  {
    label: 'Documents',
    permissions: [{ key: 'documents.review', label: 'Valider les documents' }],
  },
  {
    label: 'Paiements & factures',
    permissions: [
      { key: 'payments.view', label: 'Consulter les paiements' },
      { key: 'payments.validate', label: 'Valider les paiements et virements' },
      { key: 'invoices.view', label: 'Consulter les factures' },
    ],
  },
  {
    label: 'Planning',
    permissions: [{ key: 'planning.manage', label: 'Gérer le planning et les rendez-vous' }],
  },
  {
    label: 'Comptes rendus',
    permissions: [{ key: 'reports.manage', label: 'Gérer les comptes rendus' }],
  },
  {
    label: 'Offres',
    permissions: [{ key: 'packs.manage', label: 'Gérer les offres et tarifs' }],
  },
  {
    label: 'Partenaires',
    permissions: [{ key: 'partners.manage', label: 'Gérer les partenaires sous-traitants' }],
  },
  {
    label: 'Exports',
    permissions: [{ key: 'exports.view', label: 'Exporter les données' }],
  },
];

/** Garde seulement les valeurs reconnues (défense contre une colonne corrompue). */
export function sanitizePermissions(values: readonly string[]): Permission[] {
  const valid = new Set<string>(ALL_PERMISSIONS);
  return values.filter((v): v is Permission => valid.has(v));
}

/**
 * Permissions réellement applicables à un compte selon son rôle.
 * - super_admin → toutes
 * - admin → celles qui lui ont été cochées (assainies)
 * - autres rôles → aucune (périmètre géré par le rôle)
 */
export function effectivePermissions(
  role: UserRole,
  stored: readonly string[] | null | undefined,
): Permission[] {
  if (role === 'super_admin') return [...ALL_PERMISSIONS];
  if (role === 'admin') return sanitizePermissions(stored ?? []);
  return [];
}

/** `true` si le compte possède TOUTES les permissions requises. */
export function hasPermission(
  role: UserRole,
  permissions: readonly string[] | null | undefined,
  ...required: Permission[]
): boolean {
  if (role === 'super_admin') return true;
  const owned = new Set(permissions ?? []);
  return required.every((r) => owned.has(r));
}
